import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Tabs } from 'antd';
import { colorConstants } from 'config/colorConstants';

const scenarioTabMaxWidth = '126px';
const scenarioTabRadius = '6px 6px 0 0';
const scenarioTabInactiveBg = colorConstants.scenarioTabInactiveBg;
const scenarioTabActiveBg = colorConstants.white;
const scenarioTabActiveColor = colorConstants.violet;
const scenarioTabInactiveText = colorConstants.blackTransparent88;

export const ScenarioTabLabel = styled.span`
    display: inline-block;
    max-width: ${scenarioTabMaxWidth};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
    line-height: 22px;
    font-weight: 400;
    text-align: left;
    vertical-align: bottom;
`;

export const StyledScenarioTabsRow = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: flex-start;
    gap: 2px;
    width: calc(100% + 32px);
    margin-inline: -16px;
    min-height: 40px;
    background: ${scenarioTabInactiveBg};
`;

export const StyledScenarioTabs = styled(Tabs)`
    width: fit-content;
    max-width: 100%;
    flex: 0 0 auto;
    background: ${scenarioTabInactiveBg} !important;

    & > .ant-tabs-nav {
        margin-bottom: 0 !important;
        width: fit-content;
        max-width: 100%;
        background: ${scenarioTabInactiveBg} !important;

        &::before {
            border-bottom: 0 !important;
        }
    }

    .ant-tabs-nav-wrap,
    .ant-tabs-nav-operations {
        background: ${scenarioTabInactiveBg} !important;
    }

    &.ant-tabs-card > .ant-tabs-nav .ant-tabs-tab:not(.ant-tabs-tab-active) {
        background: ${scenarioTabInactiveBg} !important;
    }

    &.ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active {
        background: ${scenarioTabActiveBg} !important;
        border-radius: ${scenarioTabRadius} !important;
    }

    .ant-tabs-nav-wrap {
        width: fit-content !important;
        flex: 0 0 auto !important;
    }

    .ant-tabs-nav-list {
        column-gap: 2px;
    }

    .ant-tabs-tab {
        padding: 8px 16px !important;
        border: 0 !important;
        border-radius: ${scenarioTabRadius} !important;
        margin-right: 0 !important;
        margin-left: 0 !important;
        justify-content: flex-start;
        height: 40px;
        background: ${scenarioTabInactiveBg} !important;
    }

    .ant-tabs-tab .ant-tabs-tab-btn {
        width: auto;
        max-width: ${scenarioTabMaxWidth};
        color: ${scenarioTabInactiveText};
        font-size: 14px;
        line-height: 22px;
        font-weight: 400;
        text-align: left;
        overflow: hidden;
    }

    .ant-tabs-tab-active,
    .ant-tabs-tab.ant-tabs-tab-active {
        background: ${scenarioTabActiveBg} !important;
        border-radius: ${scenarioTabRadius} !important;
    }

    .ant-tabs-tab-active .ant-tabs-tab-btn,
    .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
        color: ${scenarioTabActiveColor} !important;
        font-weight: 500;
    }

    .ant-tabs-ink-bar {
        display: none;
    }

    .ant-tabs-content-holder {
        display: none;
    }
`;

export const StyledNewScenarioTabButton = styled.button`
    border: 0;
    background: ${scenarioTabInactiveBg};
    padding: 8px 16px;
    height: 40px;
    flex-shrink: 0;
    border-radius: ${scenarioTabRadius};
    color: ${scenarioTabActiveColor};
    font-size: 14px;
    line-height: 22px;
    font-weight: 400;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
        color: ${colorConstants.purpleFontPressed};
        background: ${scenarioTabInactiveBg};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
    }
`;

export const StyledScenarioMetadataSummary = styled.div`
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    padding: 0;
    border-radius: 4px;
    font-size: 12px;
    line-height: 22px;
    color: ${colorConstants.colorTextDescription};

    .metadata-label {
        font-weight: 400;
        color: inherit;
    }

    .metadata-value {
        font-weight: 400;
        margin-left: 4px;
        color: inherit;
    }
`;

export const StyledMetadataTooltipCard = styled.div`
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    width: 100%;

    .tooltip-title {
        font-size: 17px;
        font-weight: 500;
        line-height: normal;
        color: ${colorConstants.blackAlt};
    }

    .tooltip-divider {
        width: 100%;
        height: 1px;
        margin: 0;
        background: ${colorConstants.tooltipDividerGrey};
    }

    .tooltip-fields {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
        width: 100%;
    }

    .tooltip-field {
        display: grid;
        grid-template-columns: 140px 1fr;
        align-items: flex-start;
        gap: 4px;
        width: 100%;
    }

    .tooltip-field-label {
        font-size: 14px;
        line-height: 22px;
        color: ${colorConstants.toolbarLabelGrey};
    }

    .tooltip-field-value {
        font-size: 14px;
        line-height: 22px;
        color: ${colorConstants.blackTransparent88};
    }

    .tooltip-visibility-value {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 14px;
        line-height: 22px;
        color: ${colorConstants.blackTitle};

        .anticon {
            font-size: 14px;
            color: ${colorConstants.blackTitle};
        }
    }
`;

export const StyledScenarioPanel = styled.div`
    border: none;

    .toolbar-btn.ant-btn {
        height: 32px;
        border-radius: 6px;
        font-size: 14px;
        border-color: ${colorConstants.toolbarBorderGrey};
        display: flex;
        justify-content: center;
        align-items: center;
        color: ${colorConstants.black};
    }

    .toolbar-btn.ant-btn:hover {
        height: 32px;
        border-radius: 6px;
        font-size: 14px;
        border-color: ${colorConstants.purpleFontPressed};
        display: flex;
        justify-content: center;
        align-items: center;
        color: ${colorConstants.purpleFontPressed};
    }

    .toolbar-icon-btn.ant-btn {
        width: 32px;
        height: 32px;
        border-radius: 6px;
        border-color: ${colorConstants.toolbarBorderGrey};
        padding: 5px;
        color: ${colorConstants.black};
        justify-content: center;
        align-items: center;
        display: flex;
    }

    .toolbar-icon-btn.ant-btn:hover {
        width: 32px;
        height: 32px;
        border-radius: 6px;
        border-color: ${colorConstants.purpleFontPressed};
        padding: 5px;
        color: ${colorConstants.purpleFontPressed};
        justify-content: center;
        align-items: center;
        display: flex;
    }

    .toolbar-reset-btn.ant-btn:not([disabled]) {
        color: ${colorConstants.toolbarLabelGrey};
        transition: color 0.15s ease, border-color 0.15s ease;
    }

    .toolbar-reset-btn.ant-btn:not([disabled]):hover,
    .toolbar-reset-btn.ant-btn:not([disabled]):focus {
        color: ${colorConstants.purpleFontPressed} !important;
        border-color: ${colorConstants.purpleFontPressed} !important;
    }

    .toolbar-select.ant-select .ant-select-selector {
        height: 30px !important;
        border-radius: 6px !important;
        border-color: ${colorConstants.toolbarBorderGrey} !important;
    }

    .toolbar-select.ant-select:hover .ant-select-selector {
        border-color: ${colorConstants.purpleFontPressed} !important;
    }

    .toolbar-select.ant-select:hover .ant-select-selection-item,
    .toolbar-select.ant-select:hover .ant-select-arrow {
        color: ${colorConstants.purpleFontPressed} !important;
    }

    .toolbar-select.ant-select .ant-select-selection-item {
        line-height: 28px !important;
        font-size: 13px;
    }

`;

// @code-guard: skip-next-line reason="shadow token; matches tailwind.config boxShadow.moreActionsDropdown — used as AntD overlayStyle string where Tailwind class cannot be applied"
export const MORE_ACTIONS_DROPDOWN_BOX_SHADOW = '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)';

export const MORE_ACTIONS_SUBMENU_POPUP_CLASS = 'more-actions-dropdown-submenu-popup';

export const moreActionsGlobalStyles = css`
    .metadata-tooltip-no-arrow .ant-tooltip-arrow {
        display: none;
    }
    .more-actions-confirm-popover.ant-popover-placement-bottomRight
        > .ant-popover-content
        > .ant-popover-arrow {
        right: 5px !important;
    }
    .more-actions-confirm-popover.popover-padding-remove .ant-popover-inner-content {
        margin-top: 0 !important;
    }
    .simulator-filter-popover {
        padding-top: 0 !important;
    }
    .simulator-filter-popover .ant-popover-inner {
        padding: 0;
        border: none;
        box-shadow: none;
        background: transparent;
    }
    .simulator-filter-popover .ant-popover-inner-content {
        padding: 0;
        margin-top: 0;
    }
    .more-actions-dropdown .ant-dropdown-menu-item-disabled {
        pointer-events: auto;
        cursor: not-allowed;
    }
    .more-actions-dropdown .ant-dropdown-menu-submenu-title {
        display: flex !important;
        align-items: center !important;
        gap: 8px !important;
        height: 32px !important;
        padding: 5px 12px !important;
        margin: 0 !important;
        line-height: 22px !important;
    }
    .more-actions-dropdown .ant-dropdown-menu-submenu-title .ant-dropdown-menu-title-content {
        font-size: 14px !important;
        line-height: 22px !important;
        color: ${colorConstants.blackTransparent88} !important;
    }
    .more-actions-dropdown .ant-dropdown-menu-submenu-arrow,
    .more-actions-dropdown .ant-dropdown-menu-submenu-expand-icon {
        display: none !important;
    }
    .ant-dropdown-menu-submenu-popup.${MORE_ACTIONS_SUBMENU_POPUP_CLASS} {
        transform: translateX(calc(-100% - 8px)) !important;
        margin: 0 !important;
        padding: 0 !important;
        background: transparent !important;
        box-shadow: none !important;
    }
    .ant-dropdown-menu-submenu-popup.${MORE_ACTIONS_SUBMENU_POPUP_CLASS}::before,
    .ant-dropdown-menu-submenu-popup.${MORE_ACTIONS_SUBMENU_POPUP_CLASS}::after {
        display: none !important;
    }
    .more-actions-dropdown .ant-dropdown-menu,
    .ant-dropdown-menu-submenu-popup.${MORE_ACTIONS_SUBMENU_POPUP_CLASS} ul.ant-dropdown-menu {
        margin: 0 !important;
        padding: 4px !important;
        border: none !important;
        outline: none !important;
        border-radius: 8px !important;
        background: ${colorConstants.white} !important;
        box-shadow: ${MORE_ACTIONS_DROPDOWN_BOX_SHADOW} !important;
    }
    .more-actions-dropdown .ant-dropdown-menu-item,
    .more-actions-dropdown .ant-dropdown-menu-submenu,
    .${MORE_ACTIONS_SUBMENU_POPUP_CLASS} .ant-dropdown-menu-item {
        width: 100% !important;
    }
    .${MORE_ACTIONS_SUBMENU_POPUP_CLASS} .ant-dropdown-menu-item,
    .more-actions-dropdown .ant-dropdown-menu-item {
        display: flex !important;
        height: 32px !important;
        padding: 5px 12px !important;
        align-items: center !important;
        gap: 8px !important;
        font-size: 14px !important;
        line-height: 22px !important;
        border-radius: 4px !important;
        margin: 0 !important;
    }
    .${MORE_ACTIONS_SUBMENU_POPUP_CLASS} .ant-dropdown-menu-item .ant-dropdown-menu-title-content,
    .more-actions-dropdown .ant-dropdown-menu-item .ant-dropdown-menu-title-content {
        font-size: 14px !important;
        line-height: 22px !important;
        color: ${colorConstants.blackTransparent88} !important;
    }
    .${MORE_ACTIONS_SUBMENU_POPUP_CLASS} .ant-dropdown-menu-item-icon,
    .more-actions-dropdown .ant-dropdown-menu-item-icon {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        min-width: 16px !important;
        margin-right: 0 !important;
        font-size: 14px !important;
    }
    .${MORE_ACTIONS_SUBMENU_POPUP_CLASS} .ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled):hover,
    .${MORE_ACTIONS_SUBMENU_POPUP_CLASS}
        .ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled).ant-dropdown-menu-item-active,
    .more-actions-dropdown .ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled):hover,
    .more-actions-dropdown
        .ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled).ant-dropdown-menu-item-active,
    .more-actions-dropdown .ant-dropdown-menu-submenu-title:hover,
    .more-actions-dropdown .ant-dropdown-menu-submenu-title.ant-dropdown-menu-submenu-title-active {
        background-color: ${colorConstants.tabBackground} !important;
        border-radius: 4px !important;
    }
    .more-actions-dropdown .ant-dropdown-menu-item-danger .ant-dropdown-menu-title-content {
        color: ${colorConstants.growthDown} !important;
    }
    .more-actions-dropdown .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover,
    .more-actions-dropdown
        .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled).ant-dropdown-menu-item-active {
        background-color: ${colorConstants.tabBackground} !important;
        color: ${colorConstants.growthDown} !important;
    }
    .more-actions-dropdown .ant-dropdown-menu-item-disabled:hover,
    .more-actions-dropdown .ant-dropdown-menu-item-disabled.ant-dropdown-menu-item-active {
        background: transparent !important;
    }
    .more-actions-dropdown .ant-dropdown-menu-item-danger.ant-dropdown-menu-item-disabled .ant-dropdown-menu-title-content {
        color: ${colorConstants.breadcrumbSlash} !important;
    }
    .more-actions-dropdown .ant-dropdown-menu-item-divider,
    .more-actions-dropdown .ant-dropdown-menu-item-divider:hover {
        margin: 4px 0 !important;
        background-color: ${colorConstants.greyAlt2} !important;
    }
    .more-actions-dropdown
        .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover
        .ant-dropdown-menu-title-content,
    .more-actions-dropdown
        .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover
        .ant-dropdown-menu-item-icon {
        color: ${colorConstants.growthDown} !important;
        opacity: 1 !important;
    }
    .more-actions-dropdown
        .ant-dropdown-menu-item-danger.ant-dropdown-menu-item-disabled:hover
        .ant-dropdown-menu-title-content,
    .more-actions-dropdown
        .ant-dropdown-menu-item-danger.ant-dropdown-menu-item-disabled.ant-dropdown-menu-item-active
        .ant-dropdown-menu-title-content {
        color: ${colorConstants.breadcrumbSlash} !important;
    }
`;
